// // 普通POST请求（单参数）
// request({
//   url: '/api/xxx',
//   method: 'post',
//   params: { id: 123 }
// }, {
//   trimType: 'safe'
// })

// // 多参数POST（集合场景）
// request({
//   url: '/api/xxx',
//   method: 'post',
//   params: { list: [1,2,3], name: 'test' }
// }, {
//   requestType: 'Multi-parameter-post-request',
//   useCache: true // 请求成功后缓存
// })

// // 带缓存的GET请求（优先读缓存）
// request({
//   url: '/api/xxx',
//   method: 'get',
//   params: { type: 1 }
// }, {
//   cache: true // 优先读取缓存
// })

// // 文件下载请求
// request({
//   url: '/api/download',
//   method: 'get'
// }, {
//   responseType: 'blob',
//   unTimeOut: true // 禁用超时
// })

import { HttpErrInfo } from '@/constants/tips.ts'
import showMessage from '@/layouts/components/Message/index.ts'
// import VueI18n from '@/locales/index'
import { useUserStoreHook } from '@/store/modules/user'
import { deepTrimObjectValues, safeTrimObjectValues } from '@/utils/data'
import IndexDB from '@/utils/storage'
import { isObject } from '@/utils/validate'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import { ElMessage } from 'element-plus'
import { get, merge } from 'lodash-es'
import qs from 'qs'
import { getToken } from './cache/cookies'
// 国际化
import { t } from '@/locales/index.ts'; // 导入全局 t 函数

// ===================== 常量定义 =====================
// const t = VueI18n.global.t
// console.log('t == ', t)
// export const t = t
// 请求实例配置
export const axiosCreateOption = {
  baseURL: import.meta.env.VITE_BASE_API,
}

// 环境常量
const VITE_APP_ENV = import.meta.env.VITE_USER_NODE_ENV
const BASE_API = import.meta.env.VITE_BASE_API

// HTTP状态码
const HTTP_STATUS = {
  OK: 200,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REQUEST_TIMEOUT: 408,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  HTTP_VERSION_NOT_SUPPORTED: 505,
  TOKEN_EXPIRED: 901,
  ACCOUNT_LOGIN_OTHER_PLACE: 902,
} as const

// Content-Type常量
const CONTENT_TYPE = {
  JSON: 'application/json;charset=UTF-8',
  FORM_URLENCODED: 'application/x-www-form-urlencoded',
  PROBLEM_JSON: 'application/problem+json; charset=utf-8',
} as const

// 请求选项类型扩展
interface RequestOptions extends Record<string, any> {
  /** 是否开启缓存（优先读取缓存） */
  cache?: boolean
  /** 是否写入缓存（请求成功后缓存） */
  useCache?: boolean
  /** 是否禁用超时（0为不设置超时） */
  unTimeOut?: boolean
  /** 参数去空格类型：safe(浅) | deep(深) */
  trimType?: 'safe' | 'deep'
  /** POST请求参数类型：处理集合参数的特殊标识 */
  requestType?: 'Multi-parameter-post-request'
  /** 响应类型 */
  responseType?: AxiosRequestConfig['responseType']
}

// 扩展Axios配置类型
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  cache?: boolean
  useCache?: boolean
}

// ===================== 核心工具函数 =====================
/**
 * 退出登录并刷新页面
 */
const logout = (): void => {
  useUserStoreHook().logout()
  // location.reload() // 如需刷新可取消注释
}

/**
 * 获取当日零点时间戳（缓存有效期基准）
 */
const getTodayZeroTime = (): number => {
  return new Date(new Date().setHours(0, 0, 0, 0)).getTime()
}

/**
 * 生成缓存Key（URL + 参数拼接）
 */
const generateCacheKey = (
  url: string,
  params?: Record<string, any>,
): string => {
  if (!params) return url
  const paramsStr = Object.entries(params)
    .map(([key, value]) => `&${key}=${value}`)
    .join('')
  return url + paramsStr
}

/**
 * 缓存读取逻辑
 */
const getCacheData = async (
  url: string,
  params?: Record<string, any>,
): Promise<any> => {
  const key = generateCacheKey(url, params)
  const todayZeroTime = getTodayZeroTime()

  try {
    const cacheData = await IndexDB.get(key)
    if (!cacheData?.content) return Promise.reject()

    const cacheContent = JSON.parse(cacheData.content)
    // 校验缓存有效性：当日缓存 + 有有效数据
    const isCacheValid =
      cacheContent.tt === todayZeroTime &&
      cacheContent.data &&
      (Array.isArray(cacheContent.data)
        ? cacheContent.data.length
        : Object.keys(cacheContent.data).length)

    if (isCacheValid) {
      return cacheContent.data
    } else {
      await IndexDB.remove(key)
      return Promise.reject()
    }
  } catch (error) {
    return Promise.reject()
  }
}

/**
 * 缓存写入逻辑
 */
const setCacheData = async (
  url: string,
  params?: Record<string, any>,
  data?: any,
): Promise<void> => {
  const key = generateCacheKey(url, params)
  const todayZeroTime = getTodayZeroTime()
  await IndexDB.add(key, JSON.stringify({ tt: todayZeroTime, data }))
}

/**
 * 参数去空格处理
 */
const trimParams = (data: any, trimType: 'safe' | 'deep' = 'safe'): any => {
  if (Array.isArray(data)) return data
  if (!isObject(data)) return data

  return trimType === 'deep'
    ? deepTrimObjectValues(data)
    : safeTrimObjectValues(data)
}

/**
 * 处理POST/PUT请求的Content-Type和参数序列化
 */
const handlePostPutConfig = (
  method: string,
  params: Record<string, any>,
  requestType?: string,
): { contentType: string; data: any } => {
  // 判断是否为单参数场景（对象且仅一个属性）
  const isSingleParam = isObject(params) && Object.keys(params).length === 1
  const isMultiParamPost = requestType === 'Multi-parameter-post-request'

  // 单参数POST且非多参数场景 → 表单编码；其他情况 → JSON
  if (method.toUpperCase() === 'POST' && isSingleParam && !isMultiParamPost) {
    return {
      contentType: CONTENT_TYPE.FORM_URLENCODED,
      data: qs.stringify(params),
    }
  }

  // PUT请求单参数 → 表单编码
  if (method.toUpperCase() === 'PUT' && isSingleParam) {
    return {
      contentType: CONTENT_TYPE.FORM_URLENCODED,
      data: params,
    }
  }

  // 默认JSON编码
  return {
    contentType: CONTENT_TYPE.JSON,
    data: params,
  }
}

/**
 * 响应结果处理
 */
const handleResponse = (response: AxiosResponse): any => {
  const { config, data: apiData, status, headers } = response
  const { useCache } = config as CustomAxiosRequestConfig

  // 处理缓存写入
  if (useCache) {
    const params =
      config.method?.toUpperCase() === 'GET' ? config.params : config.data
    setCacheData(config.url!, params, apiData).catch(() => {})
  }

  // 二进制数据（文件下载）处理
  if (['blob', 'arraybuffer'].includes(config.responseType!)) {
    // 解析下载文件名
    if (headers['content-disposition']) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(headers['content-disposition'])
      if (matches?.[1]) {
        apiData.filename = decodeURIComponent(matches[1].replace(/['"]/g, ''))
      }
    }
    return apiData
  }

  // 非本系统接口（无状态码）
  if (status === undefined) {
    ElMessage.error(t('tips.non_system_interface'))
    return Promise.reject(new Error(t('tips.non_system_interface')))
  }

  // 业务状态码处理
  switch (status) {
    case HTTP_STATUS.OK:
      return apiData
    case HTTP_STATUS.NO_CONTENT:
      return
    case HTTP_STATUS.UNAUTHORIZED:
      logout()
      return Promise.reject(new Error(t('tips.token_expired')))
    default:
      ElMessage.error(apiData.message || t('tips.request_error'))
      return Promise.reject(
        new Error(apiData.message || t('tips.request_error')),
      )
  }
}

/**
 * 错误处理
 */
const handleError = (error: any): Promise<never> => {
  const status = get(error, 'response.status')
  const businessErrorType = error?.response?.data?.businessErrorType
  const errorMsg =
    HttpErrInfo?.[businessErrorType] || error?.response?.data?.message

  // 状态码对应提示
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      error.message = errorMsg || t('tips.bad_request')
      break
    case HTTP_STATUS.UNAUTHORIZED:
      error.message = t('tips.relogin')
      logout()
      break
    case HTTP_STATUS.NOT_FOUND:
      error.message = `${t('tips.request_404')} - ${error?.config?.url}`
      break
    case HTTP_STATUS.REQUEST_TIMEOUT:
      error.message = t('tips.request_timeout')
      break
    case HTTP_STATUS.BAD_GATEWAY:
      error.message = t('tips.system_unavailable')
      break
    case HTTP_STATUS.TOKEN_EXPIRED:
      error.message = t('tips.login_invalid')
      logout()
      break
    case HTTP_STATUS.ACCOUNT_LOGIN_OTHER_PLACE:
      error.message = t('tips.account_other_login')
      logout()
      break
    // 其他状态码可按需补充
    default:
      error.message = error.message || t('tips.unknown_error')
  }

  // 网络错误处理
  if (!error?.response && error?.code === 'ERR_NETWORK') {
    error.message = t('tips.err_network')
  }

  // 统一错误提示
  if (error.message) {
    showMessage({ message: error.message, type: 'error' })
  }

  return Promise.reject(error)
}

// ===================== Axios实例创建 =====================
/**
 * 创建Axios实例
 */
const createAxiosInstance = (): AxiosInstance => {
  const service = axios.create({
    baseURL: BASE_API,
    timeout: 30000,
    headers: {
      post: { 'Content-Type': CONTENT_TYPE.JSON },
      put: { 'Content-Type': CONTENT_TYPE.FORM_URLENCODED },
    },
  })

  // 请求拦截器
  service.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
      const token = getToken()
      // 携带Token
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      // 开发环境添加Env头
      if (VITE_APP_ENV === 'development') {
        config.headers.Env = 'test'
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // 响应拦截器
  service.interceptors.response.use(handleResponse, handleError)

  return service
}

// ===================== 请求方法封装 =====================
/**
 * 创建请求方法
 */
const createRequest = (service: AxiosInstance) => {
  return async function <T>(
    config: AxiosRequestConfig,
    options: RequestOptions = {},
  ): Promise<T> {
    // 优先读取缓存
    if (options.cache) {
      try {
        const cacheData = await getCacheData(
          config.url!,
          config.params || config.data,
        )
        return cacheData as T
      } catch {
        // 缓存不存在则走正常请求
      }
    }

    // 处理POST/PUT的Content-Type和参数
    const { method, params = {} } = config
    const requestConfig = { ...config }
    if (['post', 'put', 'POST', 'PUT'].includes(method!)) {
      const { contentType, data } = handlePostPutConfig(
        method!,
        params,
        options.requestType,
      )
      requestConfig.data = trimParams(data, options.trimType)
      requestConfig.headers = {
        ...requestConfig.headers,
        'Content-Type': contentType,
      }
    } else {
      // GET等其他请求：参数去空格
      requestConfig.params = trimParams(params, options.trimType)
    }

    // 合并基础配置
    const defaultConfig: CustomAxiosRequestConfig = {
      timeout: options.unTimeOut ? 0 : 30000,
      responseType: options.responseType,
      cache: options.cache,
      useCache: options.useCache,
    }

    const mergeConfig = merge({}, defaultConfig, requestConfig)

    return service(mergeConfig)
  }
}

// ===================== 导出实例 =====================
const axiosInstance = createAxiosInstance()
export const request = createRequest(axiosInstance)

// 补充国际化提示（建议移到@/locales/lang/xxx.ts）
// 示例：
// tips: {
//   non_system_interface: '非本系统的接口',
//   bad_request: '请求错误',
//   relogin: '请重新登录',
//   request_404: '请求地址404',
//   request_timeout: '请求超时',
//   system_unavailable: '系统暂不可用，请稍后重试或联系工作人员',
//   login_invalid: '登陆信息已失效，请重新登录',
//   account_other_login: '账号在其他地方登录',
//   unknown_error: '未知错误',
//   err_network: '网络异常，请检查网络连接',
//   request_error: '请求失败'
// }
